import React from 'react';
import { RiLockLine } from 'react-icons/ri';
import ValidatedInput from '../ui/ValidatedInput';
import SearchableSelect from '../ui/SearchableSelect';
import type { AuthorizedSignerForm } from '../../services/authorizedSignerService';

/**
 * ช่องกรอกของผู้มีอำนาจลงนาม — ยกมาจากโมดัลเดิมของ AuthorizedSignerPage ครบทุกช่อง
 * (บริษัท · ชื่อ-นามสกุล · ตำแหน่ง · อีเมล · เบอร์โทรศัพท์ · สถานะ · ผู้ลงนามหลัก)
 *
 * `companyLocked` (หน้าแก้ไข): บริษัทแสดงแบบอ่านอย่างเดียว — backend ไม่รับ company_id ตอนแก้ไข
 * (AuthorizedSignerUpdate) ถ้าให้เลือกได้ผู้ใช้จะเข้าใจว่าย้ายบริษัทแล้วทั้งที่ไม่ได้ย้าย
 */
export interface AuthorizedSignerFormFieldsProps {
  formData: AuthorizedSignerForm;
  setFormData: React.Dispatch<React.SetStateAction<AuthorizedSignerForm>>;
  formErrors: Record<string, string>;
  setFormErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  companies: Array<{ id: number; name: string; code?: string }>;
  companiesLoading?: boolean;
  companyLocked?: boolean;
  /** ชื่อบริษัทที่แสดงเมื่อ companyLocked */
  companyLabel?: string;
}

const AuthorizedSignerFormFields: React.FC<AuthorizedSignerFormFieldsProps> = ({
  formData, setFormData, formErrors, setFormErrors, companies, companiesLoading, companyLocked, companyLabel,
}) => {
  const set = <K extends keyof AuthorizedSignerForm>(key: K, value: AuthorizedSignerForm[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    if (formErrors[key as string]) setFormErrors((prev) => ({ ...prev, [key as string]: '' }));
  };

  return (
    <div className="space-y-4">
      <div data-name="company_id">
        <label className="block text-sm font-medium text-secondary-700 mb-1">
          บริษัท <span className="text-danger-500">*</span>
        </label>
        {companyLocked ? (
          <div data-testid="signer-company-locked">
            <p className="px-3 py-2 rounded-md border border-secondary-200 bg-secondary-50 text-sm text-secondary-900 [overflow-wrap:anywhere] flex items-center gap-2">
              <RiLockLine className="h-4 w-4 text-secondary-400 flex-shrink-0" />
              {companyLabel || '-'}
            </p>
            <p className="mt-1 text-xs text-secondary-500">
              เปลี่ยนบริษัทของผู้ลงนามไม่ได้ — หากผู้ลงนามย้ายบริษัท ให้เพิ่มผู้ลงนามใหม่ในบริษัทนั้น แล้วปิดใช้งานรายการนี้
            </p>
          </div>
        ) : (
          <SearchableSelect
            options={companies.map((c) => ({ value: c.id, label: c.name }))}
            value={formData.company_id || ''}
            onChange={(value) => set('company_id', value ? Number(value) : 0)}
            placeholder={companiesLoading ? 'กำลังโหลด...' : 'เลือกบริษัท'}
            disabled={companiesLoading}
            error={formErrors.company_id}
          />
        )}
      </div>

      <ValidatedInput
        label="ชื่อ-นามสกุล"
        name="full_name"
        value={formData.full_name}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('full_name', e.target.value)}
        error={formErrors.full_name}
        required
        placeholder="ชื่อ-นามสกุล"
      />

      <ValidatedInput
        label="ตำแหน่ง"
        name="position"
        value={formData.position}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('position', e.target.value)}
        error={formErrors.position}
        required
        placeholder="ตำแหน่ง"
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ValidatedInput
          label="อีเมล"
          name="email"
          type="email"
          value={formData.email || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('email', e.target.value)}
          error={formErrors.email}
          placeholder="example@company.com"
        />
        <ValidatedInput
          label="เบอร์โทรศัพท์"
          name="phone"
          type="tel"
          value={formData.phone || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => set('phone', e.target.value)}
          error={formErrors.phone}
          placeholder="0x-xxxx-xxxx"
        />
      </div>

      <div>
        <label htmlFor="signer-is-active" className="block text-sm font-medium text-secondary-700 mb-1">
          สถานะ <span className="text-danger-500">*</span>
        </label>
        <select
          id="signer-is-active"
          name="is_active"
          value={formData.is_active ? 'true' : 'false'}
          onChange={(e) => set('is_active', e.target.value === 'true')}
          className="w-full px-3 py-2 min-h-[44px] sm:min-h-0 border border-secondary-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          <option value="true">ใช้งาน</option>
          <option value="false">ไม่ใช้งาน</option>
        </select>
      </div>

      <div>
        <label className="flex items-center min-h-[44px] sm:min-h-0">
          <input
            type="checkbox"
            name="is_primary"
            checked={formData.is_primary || false}
            onChange={(e) => set('is_primary', e.target.checked)}
            className="mr-2 h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded"
          />
          <span className="text-sm font-medium text-secondary-700">ผู้ลงนามหลัก (สำหรับสัญญา)</span>
        </label>
        <p className="text-xs text-secondary-500 mt-1">ผู้ลงนามหลักจะถูกใช้ในการสร้าง PDF สัญญาโดยอัตโนมัติ</p>
      </div>
    </div>
  );
};

export default AuthorizedSignerFormFields;
